import { inject, Injectable } from '@angular/core';
import { lastValueFrom } from 'rxjs';
import { v4 as uuidV4 } from 'uuid';
import { TrackingEvent } from '../../../cross-team-shared/event-tracking/event-utils';
import { MessagingService } from '../../../cross-team-shared/messaging/messaging.service';
import { EventTrackingService } from '../../../shared/event-tracking/event-tracking.service';
import { WorkspaceConfigService } from '../../../shared/misc-services/workspace-config.service';
import { WorkspaceLoadingService } from '../../../shared/misc-services/workspace-loading.service';
import { ExecutionQueueService } from '../../../shared/save-manager/execution-queue.service';
import { PeriodicSaveManagerService } from '../../../shared/save-manager/periodic-save-manager.service';
import { AnswersByName } from '../../../team-apis/otterdam/answers/answer';
import { FieldFeedbackExternal } from '../../../team-apis/otterdam/field-feedback';
import { IndividualFieldScore } from '../../../team-apis/otterdam/individual-field-score';
import { isAnnotationOutput } from '../../h3ml/dsl/sensor-fusion/sensor-fusion-output.utils';
import { TaskWorkspaceStore } from '../../h3ml/state/task-workspace-store';
import { DraftSubmissionService } from '../../production-workspace/submissions/draft-submission.service';
import {
  SubmitTaskService,
  TaskSubmissionBenignError,
  TaskSubmissionError,
} from '../../production-workspace/submissions/submit-task.service';
import { isRedirectResponse } from '../navigation/is-redirect-response';
import { TrainingTaskResourceService } from '../resources/training-task-resource.service';
import { isSubmissionErrors } from '../utils/submission-errors-utils';

@Injectable()
export class TrainingTaskServiceObjectless {
  private draftSubmissionService = inject(DraftSubmissionService);
  private eventTrackingService = inject(EventTrackingService);
  private executionQueue = inject(ExecutionQueueService);
  private messagingService = inject(MessagingService);
  private periodicSaveManager = inject(PeriodicSaveManagerService);
  private taskWorkspaceStore = inject(TaskWorkspaceStore);
  private trainingTaskResource = inject(TrainingTaskResourceService);
  private submitTaskService = inject(SubmitTaskService);
  private workspaceConfigService = inject(WorkspaceConfigService);
  private workspaceLoadingService = inject(WorkspaceLoadingService);

  async calculateScoreObjectless(event: TrackingEvent & { source?: string }) {
    const { task } = this.taskWorkspaceStore.state();
    if (task.round <= 1) {
      const isSubmissionSuccessful =
        await this.handleTaskSubmissionObjectless();
      if (!isSubmissionSuccessful) {
        return;
      }
    }

    this.eventTrackingService.track(event);
    const { answers, errors } = this.submitTaskService.transformAnswers();
    if (errors.length) {
      throw new Error('Error while transforming answers');
    }

    await this.calculateAndStoreScoreObjectless(answers);
  }

  async calculateAndStoreScoreObjectless(answers: AnswersByName) {
    const { score, shape_feedback, field_feedback } = await lastValueFrom(
      this.trainingTaskResource.calculateScoreObjectless$(answers),
    );
    shape_feedback.forEach((feedback) => {
      feedback.feedbackId = uuidV4();
    });
    this.taskWorkspaceStore.batch(() => {
      this.taskWorkspaceStore('outputTrainingScore').set(score);
      this.taskWorkspaceStore('feedbackType').set('shape');
      this.taskWorkspaceStore('shapeFeedback').set(shape_feedback);
      this.updateFeedbackForNonAnnotationOutputs(field_feedback);
    });
  }

  showNonAnnotationHints() {
    const nonAnnotationOutputs = this.taskWorkspaceStore
      .state()
      .project.outputs.filter((output) => !isAnnotationOutput(output));
    const trainingAnswers =
      this.taskWorkspaceStore.state().task._extras.training?.answers;
    const outputInfoByNameStore = this.taskWorkspaceStore('outputInfoByName');

    this.taskWorkspaceStore.batch(() => {
      nonAnnotationOutputs.forEach((output) => {
        outputInfoByNameStore(output.name).assign({
          trainingAnswer: trainingAnswers?.[output.name],
        });
      });
    });
  }

  hideNonAnnotationHints() {
    const nonAnnotationOutputs = this.taskWorkspaceStore
      .state()
      .project.outputs.filter((output) => !isAnnotationOutput(output));
    const outputInfoByNameStore = this.taskWorkspaceStore('outputInfoByName');

    this.taskWorkspaceStore.batch(() => {
      nonAnnotationOutputs.forEach((output) => {
        outputInfoByNameStore(output.name)('trainingAnswer').delete();
      });
    });
  }

  private async handleTaskSubmissionObjectless() {
    try {
      await this.submitTaskAndIncrementRoundLocallyObjectless();
    } catch (error) {
      if (error instanceof TaskSubmissionError) {
        this.messagingService.error(error.message);
        return false;
      } else if (error instanceof TaskSubmissionBenignError) {
        return false;
      }

      throw error;
    }

    return true;
  }

  private async submitTaskAndIncrementRoundLocallyObjectless() {
    this.workspaceLoadingService.isLoading$.next(true);
    const qaGroupId = this.workspaceConfigService.qaGroupId ?? '';

    try {
      await this.executionQueue.enqueue(() =>
        this.handleSubmitObjectless(qaGroupId),
      );
      this.workspaceLoadingService.isLoading$.next(false);
      this.enableWorkAndDraftSaves();
    } catch (e) {
      if (!(e instanceof TaskSubmissionError)) {
        this.enableWorkAndDraftSaves();
      }
      throw e;
    }
  }

  private async handleSubmitObjectless(qaGroupId: string) {
    this.periodicSaveManager.stop();
    const result = await this.submitTaskService.submit(qaGroupId);
    if (isSubmissionErrors(result)) {
      if (result.serverErrors.length) {
        throw new TaskSubmissionError(
          'Error while submitting a task. Please reload the page.',
        );
      }

      throw new TaskSubmissionBenignError();
    }

    if (isRedirectResponse(result)) {
      throw new Error('Got redirect response, but expected a task.', {
        cause: `redirect_to: ${result.redirect_to}, message: ${result.message}`,
      });
    }

    if (result.id !== this.taskWorkspaceStore.state().task.id) {
      throw new Error('Expected to receive the same task with round 2.');
    }

    this.taskWorkspaceStore('task')('round').set(result.round);
    this.draftSubmissionService.resetSavedAnswers();
  }

  private updateFeedbackForNonAnnotationOutputs(
    fieldFeedbacks: FieldFeedbackExternal[],
  ) {
    const individualFieldScore = Object.fromEntries<IndividualFieldScore>(
      fieldFeedbacks.map((feedback) => [
        feedback.outputId,
        { score: false, error_tags: feedback.errorTags },
      ]),
    );
    this.taskWorkspaceStore('individualFieldScore').set(individualFieldScore);
  }

  private enableWorkAndDraftSaves() {
    this.periodicSaveManager.start();
    this.workspaceLoadingService.isLoading$.next(false);
  }
}
